import { useMemo } from 'react';

import TextDisplayBlock from '@apigrowfinance/pbolt-text-display-block';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { ITimelineItem } from '@clients/timelineClient';
import accountTimelineCategoryDesc from '@messages/accountTimelineCategoryDesc';
import getEnumTranslation from '@utils/getEnumTranslation';
import { toTitleCase } from '@utils/string/caseUtils';

interface TimelineDetailsBodyCardProps {
    timelineItem: ITimelineItem;
    isLoading?: boolean;
}

const TimelineDetailsBodyTransaction = ({
    timelineItem,
    isLoading,
}: TimelineDetailsBodyCardProps) => {
    const { formatMessage } = useIntl();

    const textBlocks = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: 'TimelineDetails.description',
                    defaultMessage: 'Description',
                }),
                value: timelineItem?.category
                    ? (getEnumTranslation(timelineItem?.category + '_DESC', {
                          messages: accountTimelineCategoryDesc,
                          formatMessage,
                      }) as string)
                    : '',
                width: 12,
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.type',
                    defaultMessage: 'Type',
                }),
                value: toTitleCase(timelineItem?.type),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.category',
                    defaultMessage: 'Category',
                }),
                value: toTitleCase(timelineItem?.category),
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.authorisationCode',
                    defaultMessage: 'Authorization Code',
                }),
                value: timelineItem?.data?.item?.authorization_code,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.contractAmount',
                    defaultMessage: 'Contract Amount',
                }),
                value: timelineItem?.data?.item?.contract_amount,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.responseCode',
                    defaultMessage: 'Response Code',
                }),
                value: toTitleCase(timelineItem?.data?.item?.response_code),
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.network',
                    defaultMessage: 'Network',
                }),
                value: toTitleCase(timelineItem?.data?.item?.network),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.cardType',
                    defaultMessage: 'Card Type',
                }),
                value: toTitleCase(timelineItem?.data?.item?.card_type),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.cardId',
                    defaultMessage: 'Card Id',
                }),
                value: timelineItem?.data?.item?.card_id,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.cardName',
                    defaultMessage: 'Card Name',
                }),
                value: toTitleCase(timelineItem?.data?.item?.card_name),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.entryModeLiteral',
                    defaultMessage: 'Entry Mode Literal',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.entry_mode_literal,
                ),
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.merchantCategoryGroup',
                    defaultMessage: 'Merchant Category Group',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.merchant_category_group,
                ),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.merchantCategoryCode',
                    defaultMessage: 'Merchant category code',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.merchant_category_code,
                ),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.merchantCity',
                    defaultMessage: 'Merchant City',
                }),
                value: toTitleCase(timelineItem?.data?.item?.merchant_city),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.merchantStateOrCountry',
                    defaultMessage: 'Merchant State Or Country',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.merchant_state_or_country,
                ),
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.validationResults',
                    defaultMessage: 'Validation Results',
                }),
                value:
                    timelineItem?.data?.item?.validation_results?.length > 0
                        ? (getEnumTranslation('yes', {
                              formatMessage: formatMessage,
                          }) as string)
                        : (getEnumTranslation('no', {
                              formatMessage: formatMessage,
                          }) as string),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.dcc',
                    defaultMessage: 'DCC',
                }),
                value: toTitleCase(timelineItem?.data?.item?.dcc),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.programId',
                    defaultMessage: 'Program Id',
                }),
                value: timelineItem?.data?.item?.program_id,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.orgOperationProcessingCode',
                    defaultMessage: 'Organisation Operation Processing Code',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.org_operation?.processing_code,
                ),
                width: 6,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.retrievalReferenceNumber',
                    defaultMessage: 'Retrieval Reference Number',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.retrieval_reference_number,
                ),
                width: 6,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.numberOfInstallments',
                    defaultMessage: 'Number of Installments',
                }),
                value: timelineItem?.data?.item?.number_of_installments,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.principalAmount',
                    defaultMessage: 'Principal Amount',
                }),
                value: timelineItem?.data?.item?.principle_amount,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.entryMode',
                    defaultMessage: 'Entry Mode',
                }),
                value: toTitleCase(timelineItem?.data?.item?.entry_mode),
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.cardholderbillingAmount',
                    defaultMessage: 'Cardholder Billing Amount',
                }),
                value: timelineItem?.data?.item?.cardholderbilling_amount,
                width: 6,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.nsu',
                    defaultMessage: 'NSU',
                }),
                value: toTitleCase(timelineItem?.data?.item?.nsu),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.preAuthorization',
                    defaultMessage: 'Pre-authorization',
                }),
                value: toTitleCase(timelineItem?.data?.item?.pre_authorization),
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.softDescriptor',
                    defaultMessage: 'Soft Descriptor',
                }),
                value: toTitleCase(timelineItem?.data?.item?.soft_descriptor),
                width: 6,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.installments',
                    defaultMessage: 'Installments',
                }),
                value: timelineItem?.data?.item?.installments
                    ? JSON.stringify(
                          timelineItem?.data?.item?.installments,
                          null,
                          2,
                      )
                    : '',
                preFormattedValue: true,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.cashbackAmount',
                    defaultMessage: 'Cashback Amount',
                }),
                value: timelineItem?.data?.item?.cash_back_amount,
                width: 6,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.airportTax',
                    defaultMessage: 'Airport Tax',
                }),
                value: timelineItem?.data?.item?.airport_tax,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.interestRate',
                    defaultMessage: 'Interest Rate',
                }),
                value: timelineItem?.data?.item?.interest_rate,
            },
            {
                label: formatMessage({
                    id: 'id',
                    defaultMessage: 'Id',
                }),
                value: timelineItem?.data?.item?.id,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.mti',
                    defaultMessage: 'MTI',
                }),
                value: toTitleCase(timelineItem?.data?.item?.mti),
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.installmentAmount',
                    defaultMessage: 'Installment Amount',
                }),
                value: timelineItem?.data?.item?.installment_amount,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.toName',
                    defaultMessage: 'To Name',
                }),
                value: toTitleCase(timelineItem?.data?.item?.to_name),
                width: 6,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.cardholderCurrencyRate',
                    defaultMessage: 'Cardholder Currency Rate',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.cardholder_currency_rate,
                ),
                width: 6,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.localAmount',
                    defaultMessage: 'Local Amount',
                }),
                value: timelineItem?.data?.item?.local_amount,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.customerId',
                    defaultMessage: 'Customer Id',
                }),
                value: timelineItem?.data?.item?.customer_id,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.customCode',
                    defaultMessage: 'Custom Code',
                }),
                value: timelineItem?.data?.item?.custom_code,
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.currencyLiteral',
                    defaultMessage: 'Currency Literal',
                }),
                value: timelineItem?.data?.item?.currency_literal,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.currencyCode',
                    defaultMessage: 'Currency Code',
                }),
                value: toTitleCase(timelineItem?.data?.item?.currency_code),
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.settlementCurrencyLiteral',
                    defaultMessage: 'Settlement Currency Literal',
                }),
                value: timelineItem?.data?.item?.settlement_currency_literal,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.settlementCurrencyCode',
                    defaultMessage: 'Settlement Currency Code',
                }),
                value: toTitleCase(
                    timelineItem?.data?.item?.settlement_currency_code,
                ),
            },

            {
                label: formatMessage({
                    id: 'TimelineDetails.settlementCurrencyAmount',
                    defaultMessage: 'Settlement Currency Amount',
                }),
                value: timelineItem?.data?.item?.settlement_currency_amount,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.settlementCurrencyRate',
                    defaultMessage: 'Settlement Currency Rate',
                }),
                value: timelineItem?.data?.item?.settlement_currency_rate,
            },
            {
                label: ' ',
                value: ' ',
                width: 12,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.correlationId',
                    defaultMessage: 'Correlation Id',
                }),
                value: timelineItem?.correlation_id,
                width: 6,
            },
            {
                label: formatMessage({
                    id: 'TimelineDetails.timestamp',
                    defaultMessage: 'Timestamp',
                }),
                value: dayjs(timelineItem?.timestamp).format(
                    'D MMM YYYY h:mmA',
                ),
                width: 6,
            },
        ];
    }, [timelineItem, formatMessage]);

    return <TextDisplayBlock isLoading={isLoading} textBlocks={textBlocks} />;
};

export default TimelineDetailsBodyTransaction;
